import moment from 'moment';

import {DataType} from 'data-frames/DataFrameTypes';

export type DataAccessor<Data> = (data: Data) => unknown;

/**
 * Generic function to create accessor functions for visualization points
 *
 * Accessor functions get a property from a point and are used in by various visualization components to define
 * how to access data from a point. For our visualization library, that means an accessor function defines how
 * to access a field from a point and how to convert it to the correct JavaScript data based on the DataType of
 * that point. For example, if the DataType is DataType.DATE_OR_TIMESTAMP, we want a Date object for the purpose
 * of viualizing dates. A lot of visx's components also expect accessors, so this allows us to quickly create
 * those.
 *
 * Example usage:
 *
 *    const xAccessor = dataAccessor<{x: unknown; y: unknown}, 'x'>('x', DataType.DATE_OR_TIMESTAMP);
 *    xAccessor({x: "2020-01-01", y: 12})
 *    // returns Date("2020-01-01")
 */
export default function dataAccessor<DataPoint, Field extends keyof DataPoint>(
  field: Field,
  fieldType: DataType
): DataAccessor<DataPoint> {
  return (data) => {
    if (fieldType === DataType.DATE_OR_TIMESTAMP) {
      return moment(data[field] as unknown as string).toDate();
    } else {
      return data[field];
    }
  };
}

/**
 * Generic function to create accessor functions for visualization points where the specified field might not
 * be defined in the dataset. Return null if the field is not defined and an accessor otherwise.
 */
export function dataAccessorForColumnIfPresent<DataPoint, Field extends keyof DataPoint>(
  field: Field,
  fieldType?: DataType
): DataAccessor<DataPoint> | null {
  return fieldType == null ? null : dataAccessor<DataPoint, Field>(field, fieldType);
}
